import { Fragment, useState } from "react";
import { Dialog, Transition } from "@headlessui/react";
import { FiX, FiEye, FiGrid, FiCalendar, FiUsers, FiTag } from "react-icons/fi";
import EnvolvementThumbnail from "./explorePage/EnvolvementThumbnail";

// This is a simplified, self-contained version of the detail view
const DetailView = ({ data }) => {
  const publishedDate = new Date(data.datePublished);

  function formatDate(string) {
    var options = { year: "numeric", month: "long", day: "numeric" };
    return new Date(string).toLocaleDateString([], options);
  }

  return (
    <div className="transform overflow-hidden rounded-xl bg-white dark:bg-gray-800 text-left shadow-xl transition-all w-full max-w-3xl mx-auto">
      {/* Image Section */}
      <div className="relative h-60 sm:h-72">
        <img
          src={data.imgUrl || "https://placehold.co/600x400?text=Your+Image"}
          className="w-full h-full object-cover"
          alt={data.title}
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent" />
        <div className="absolute bottom-0 left-0 p-4 sm:p-6">
          <h2 className="text-2xl sm:text-3xl font-extrabold text-white drop-shadow-lg">
            {data.title || "Your Title"}
          </h2>
        </div>
      </div>

      {/* Content Section */}
      <div className="px-4 sm:px-6 py-6">
        <div className="space-y-6">
          {/* Description */}
          <p className="text-base sm:text-lg text-gray-600 dark:text-gray-300">
            {data.description ||
              data.previewText ||
              "Your detailed description will appear here."}
          </p>

          {/* Categories */}
          {data.categories && data.categories.length > 0 && (
            <div className="flex flex-wrap gap-3 items-center">
              <FiTag className="text-gray-400" />
              {data.categories.map((category) => (
                <span
                  key={category}
                  className="px-3 py-1 bg-primary-100 text-primary-800 rounded-full text-sm font-medium"
                >
                  {category}
                </span>
              ))}
            </div>
          )}

          {/* Stats Grid */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 py-6 border-y border-gray-200 dark:border-gray-700">
            <div className="flex items-start space-x-4">
              <FiCalendar className="w-6 h-6 text-primary-500 flex-shrink-0 mt-1" />
              <div>
                <p className="font-semibold text-gray-900 dark:text-white">
                  {data.endDate.none ? "No Deadline" : "Ends in X days"}
                </p>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  Posted {formatDate(publishedDate)}
                </p>
              </div>
            </div>
            <div className="flex items-start space-x-4">
              <FiUsers className="w-6 h-6 text-primary-500 flex-shrink-0 mt-1" />
              <div>
                <p className="font-semibold text-gray-900 dark:text-white">
                  {data.quantity.unlimited
                    ? "Unlimited Spots"
                    : `${data.quantity.quantity} Spots`}
                </p>
                <p className="text-2xl font-bold text-primary-500 mt-1">
                  {data.price.free ? "Free to Join" : `$${data.price.price}`}
                </p>
              </div>
            </div>
          </div>

          {/* User Info */}
          <div className="flex items-center space-x-4">
            <img
              src={data.userPicture || "https://placehold.co/100x100?text=Avatar"}
              className="w-14 h-14 rounded-full object-cover"
              alt={data.username}
            />
            <div>
              <p className="text-lg font-bold text-gray-900 dark:text-white">
                {data.username || "Your Name"}
              </p>
              <p className="text-sm text-gray-500 dark:text-gray-400">
                Envolvement Creator
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default function EnvolvementPreviewModal({ open, onClose, data }) {
  const [view, setView] = useState("thumbnail"); // 'thumbnail' or 'detail'

  return (
    <Transition.Root show={open} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={onClose}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black/50 backdrop-blur-sm transition-opacity" />
        </Transition.Child>

        <div className="fixed inset-0 z-10 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4 text-center sm:p-0">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              enterTo="opacity-100 translate-y-0 sm:scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 translate-y-0 sm:scale-100"
              leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
            >
              <Dialog.Panel className="relative transform overflow-hidden rounded-2xl bg-gray-100 dark:bg-gray-900 text-left shadow-2xl transition-all sm:my-8 w-full sm:max-w-lg md:max-w-xl lg:max-w-2xl p-4 sm:p-6">
                <div className="flex justify-between items-center mb-4">
                  <Dialog.Title className="text-xl font-bold text-gray-900 dark:text-white">
                    Live Preview
                  </Dialog.Title>
                  <button
                    type="button"
                    className="rounded-full p-1 text-gray-400 hover:text-gray-600"
                    onClick={onClose}
                  >
                    <FiX className="h-6 w-6" />
                  </button>
                </div>

                <div className="bg-gray-200 dark:bg-gray-800 p-1 rounded-lg flex justify-center gap-2 mb-4">
                  <button
                    onClick={() => setView("thumbnail")}
                    className={`w-full px-4 py-2 text-sm font-medium rounded-md transition-colors ${
                      view === "thumbnail"
                        ? "bg-white dark:bg-gray-700 text-primary-600 shadow"
                        : "text-gray-600 dark:text-gray-300"
                    }`}
                  >
                    <FiGrid className="inline mr-2" />
                    Thumbnail
                  </button>
                  <button
                    onClick={() => setView("detail")}
                    className={`w-full px-4 py-2 text-sm font-medium rounded-md transition-colors ${
                      view === "detail"
                        ? "bg-white dark:bg-gray-700 text-primary-600 shadow"
                        : "text-gray-600 dark:text-gray-300"
                    }`}
                  >
                    <FiEye className="inline mr-2" />
                    Detail View
                  </button>
                </div>

                <div className="min-h-[400px] flex items-center justify-center">
                  {view === "thumbnail" ? (
                    <div className="w-full max-w-sm pointer-events-none">
                      <EnvolvementThumbnail data={data} />
                    </div>
                  ) : (
                    <div className="w-full pointer-events-none">
                      <DetailView data={data} />
                    </div>
                  )}
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition.Root>
  );
}
